import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Branch, BranchSettings, UpdateBranchInput } from '@/queries/useBranches'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

// Zod schema for branch settings validation
const branchSettingsSchema = z.object({
  voiceSelection: z.string().optional(),
  vadThreshold: z.union([z.number().min(0).max(1), z.literal('')]).optional(),
  llmModel: z.string().optional(),
  llmSystemPrompt: z.string().optional(),
})

type BranchSettingsFormData = z.infer<typeof branchSettingsSchema>

interface BranchSettingsFormProps {
  branch: Branch
  onSubmit: (data: UpdateBranchInput) => void | Promise<void>
  isLoading?: boolean
  error?: Error | null
  onCancel?: () => void
}

export default function BranchSettingsForm({
  branch,
  onSubmit,
  isLoading = false,
  error,
  onCancel,
}: BranchSettingsFormProps) {
  const { t } = useTranslation('admin')
  
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<BranchSettingsFormData>({
    resolver: zodResolver(branchSettingsSchema),
    defaultValues: {
      voiceSelection: branch.settings?.realtimeVoice || '',
      vadThreshold: (branch.settings?.realtimeVadThreshold?.toString() || '') as number | '',
      llmModel: branch.settings?.llmModel || '',
      llmSystemPrompt: branch.settings?.llmSystemPrompt || '',
    },
  })

  const handleFormSubmit = async (data: BranchSettingsFormData) => {
    // Build settings object matching backend JSONB structure
    const settings: BranchSettings = {
      ...branch.settings,
      realtimeVoice: data.voiceSelection || undefined,
      realtimeVadThreshold:
        data.vadThreshold && typeof data.vadThreshold === 'number'
          ? data.vadThreshold
          : data.vadThreshold && typeof data.vadThreshold === 'string' && data.vadThreshold !== ''
          ? parseFloat(data.vadThreshold)
          : undefined,
      llmModel: data.llmModel || undefined,
      llmSystemPrompt: data.llmSystemPrompt || undefined,
    }

    const submitData: UpdateBranchInput = {
      settings,
    }
    await onSubmit(submitData)
  }

  return (
    <form 
      onSubmit={handleSubmit(handleFormSubmit)} 
      className="flex flex-col gap-[var(--spacing-md)]"
    >
      {error && (
        <Alert variant="destructive" data-testid="branch-settings-form-error">
          <AlertDescription>
            {error.message || t('branches.form.errorOccurred')}
          </AlertDescription>
        </Alert>
      )}

      {/* Voice Settings Section */}
      <div className="flex flex-col gap-[var(--spacing-md)]">
        <h3
          className="text-[var(--text-lg)] font-semibold font-heading"
          data-testid="branch-settings-voice-section-title"
        >
          {t('branches.form.settings.voiceSettings')}
        </h3>

        <div className="flex flex-col gap-[var(--spacing-sm)]">
          <Label htmlFor="voiceSelection" data-testid="branch-settings-voice-selection-label">
            {t('branches.form.settings.voiceSelection')}
          </Label>
          <Controller
            name="voiceSelection"
            control={control}
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger
                  id="voiceSelection"
                  data-testid="branch-settings-voice-selection-input"
                >
                  <SelectValue placeholder={t('branches.form.settings.voicePlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="alloy">Alloy</SelectItem>
                  <SelectItem value="echo">Echo</SelectItem>
                  <SelectItem value="fable">Fable</SelectItem>
                  <SelectItem value="onyx">Onyx</SelectItem>
                  <SelectItem value="nova">Nova</SelectItem>
                  <SelectItem value="shimmer">Shimmer</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="flex flex-col gap-[var(--spacing-sm)]">
          <Label htmlFor="vadThreshold" data-testid="branch-settings-vad-threshold-label">
            {t('branches.form.settings.vadThreshold')}
          </Label>
          <Input
            id="vadThreshold"
            type="number"
            step="0.1"
            min="0"
            max="1"
            {...register('vadThreshold')}
            placeholder={t('branches.form.settings.vadPlaceholder')}
            data-testid="branch-settings-vad-threshold-input"
            className={errors.vadThreshold ? 'border-destructive' : ''}
          />
          {errors.vadThreshold && (
            <p
              className="text-[0.8125rem] text-[var(--error)] mt-[var(--space-1)]"
              data-testid="branch-settings-vad-threshold-error"
            >
              {errors.vadThreshold.message}
            </p>
          )}
        </div>
      </div>

      {/* LLM Settings Section */}
      <div className="flex flex-col gap-[var(--spacing-md)]">
        <h3
          className="text-[var(--text-lg)] font-semibold font-heading"
          data-testid="branch-settings-llm-section-title"
        >
          {t('branches.form.settings.llmSettings')}
        </h3>

        <div className="flex flex-col gap-[var(--spacing-sm)]">
          <Label htmlFor="llmModel" data-testid="branch-settings-llm-model-label">
            {t('branches.form.settings.model')}
          </Label>
          <Controller
            name="llmModel"
            control={control}
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger id="llmModel" data-testid="branch-settings-llm-model-input">
                  <SelectValue placeholder={t('branches.form.settings.modelPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gpt-4">GPT-4</SelectItem>
                  <SelectItem value="gpt-4-turbo">GPT-4 Turbo</SelectItem>
                  <SelectItem value="gpt-3.5-turbo">GPT-3.5 Turbo</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="flex flex-col gap-[var(--spacing-sm)]">
          <Label
            htmlFor="llmSystemPrompt"
            data-testid="branch-settings-llm-system-prompt-label"
          >
            {t('branches.form.settings.systemPrompt')}
          </Label>
          <Textarea
            id="llmSystemPrompt"
            {...register('llmSystemPrompt')}
            placeholder={t('branches.form.settings.systemPromptPlaceholder')}
            rows={4}
            data-testid="branch-settings-llm-system-prompt-input"
            className={errors.llmSystemPrompt ? 'border-destructive' : ''}
          />
          {errors.llmSystemPrompt && (
            <p
              className="text-[0.8125rem] text-[var(--error)] mt-[var(--space-1)]"
              data-testid="branch-settings-llm-system-prompt-error"
            >
              {errors.llmSystemPrompt.message}
            </p>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-[var(--spacing-sm)] rtl:justify-start pt-[var(--spacing-md)]">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting || isLoading}
            data-testid="branch-settings-form-cancel-button"
          >
            {t('branches.form.cancel')}
          </Button>
        )}
        <Button
          type="submit"
          disabled={isSubmitting || isLoading}
          data-testid="branch-settings-form-submit-button"
        >
          {isSubmitting || isLoading ? t('branches.form.saving') : t('branches.form.settings.saveSettings')}
        </Button>
      </div>
    </form>
  )
}

